import React, { useState } from "react";
import { useGetConciliatedHistoryByDivergenceQuery } from "@/services/divergence";
import { Loader2, History, User, Calendar, Receipt } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Button } from "components/ui/button";
import { ScrollArea } from "components/ui/scroll-area";
import { format } from "date-fns";
import { formatToBRL } from "@/utils/formatToMoney";

interface DivergenceHistoryPopoverProps {
  divergenceId: number;
}

const DivergenceHistoryPopover: React.FC<DivergenceHistoryPopoverProps> = ({ divergenceId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: history, isFetching } = useGetConciliatedHistoryByDivergenceQuery(
    { divergenceId },
    { skip: !isOpen }
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="h-8 w-8" title="Ver histórico de conciliação">
          {isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <History className="h-4 w-4" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0 shadow-xl border border-gray-200 bg-white rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h4 className="font-semibold text-sm text-gray-900">Histórico de Conciliação</h4>
        </div>
        {isFetching ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
          </div>
        ) : Array.isArray(history) && history.length > 0 ? (
          <ScrollArea className="h-72 w-full p-4">
            <div className="space-y-6">
              {history.map((item) => (
                <div key={item.id} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <Receipt className="w-3 h-3" /> ID: {item.id}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <User className="w-3 h-3" /> {item.created_by}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-600">
                      {item.created_at ? format(new Date(item.created_at), "dd/MM/yyyy HH:mm") : "-"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-500">Valor Total:</span>
                    <span className="font-bold text-sm text-[#402430]">
                      {formatToBRL(Number(item.conciliated_amount_value))}
                    </span>
                  </div>
                  <div className="mt-2">
                    <span className="text-xs text-gray-500 font-medium">Divergências Conciliadas:</span>
                    <div className="mt-1 space-y-1">
                      {Array.isArray(item.divergences) && item.divergences.length > 0 ? (
                        item.divergences.map((div: { id: number; name: string; amount: string; conciliation_date?: string }) => (
                          <div key={div.id} className="flex justify-between items-center bg-white rounded px-2 py-1 border border-gray-100">
                            <span className="text-xs text-gray-700 truncate max-w-[120px]" title={div.name}>{div.name}</span>
                            <span className="text-xs text-gray-500">{div.conciliation_date ? format(new Date(div.conciliation_date), "dd/MM/yyyy") : "-"}</span>
                            <span className="text-xs font-medium text-gray-900">{formatToBRL(Number(div.amount))}</span>
                          </div>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400">Nenhuma divergência encontrada</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="text-sm text-muted-foreground p-4 text-center">
            Nenhum histórico encontrado
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default DivergenceHistoryPopover;
